/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { LoginChangePasswordForm } from '@/components/content/Login/parts/LoginChangePasswordForm';
import { LoginForm } from '@/components/content/Login/parts/LoginForm';
import { loginContainerSX } from '@/components/content/Login/styles/container';
import { UserLogon, personMutatorKeyMatcher, useLogin } from '@/data/Content/Login';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC } from 'react';
import { useSWRConfig } from 'swr';

export const Login: FC<{ id: ID }> = () => {
	const {
		dimensions: { contentSpacing },
	} = useTheme();

	const router = useNextRouter();
	const RouteLocal = useLocalization('Routes');
	const { mutate } = useSWRConfig();
	const { loginSubmit, setPasswordExpired, passwordExpired } = useLogin();

	const handleLogin = async (props: UserLogon) => {
		const resp = await loginSubmit(props);
		if ('userId' in resp ?? {}) {
			if (router.query.flow === 'checkout') {
				await router.push(RouteLocal.CheckOut.route.t());
			} else {
				await router.push('/');
			}
			await mutate(personMutatorKeyMatcher(''), undefined);
		}
	};

	const onCancel = () => {
		setPasswordExpired(null);
	};
	const signInNLS = useLocalization('SignIn');
	const checkoutFlow = router.query.flow === 'checkout' ? true : false;
	const signInTitle = checkoutFlow
		? signInNLS.SignInAndCheckoutTitle.t()
		: signInNLS.SignInButton.t();

	return (
		<Grid container spacing={contentSpacing} justifyContent="center">
			<Grid item xs={12} sm={12} md={12} m="auto">
				<Typography variant="h4" mt={4} mb={4}>
					{signInTitle}
				</Typography>
				<Paper sx={loginContainerSX}>
					<Grid container mt={2} mb={4} spacing={3}>
						<Grid item xs={12} md={7} lg={6}>
							<Box pr={{ sm: 6 }} pl={{ sm: 6 }}>
								<ContentRecommendation
									key={`SignInAlert`}
									id={`SignInAlert`}
									properties={{ emsName: `SignInAlert` }}
								/>
								{passwordExpired !== null ? (
									<LoginChangePasswordForm
										onSubmit={handleLogin}
										onCancel={onCancel}
										passwordExpired={passwordExpired}
									/>
								) : (
									<LoginForm onSubmit={handleLogin} />
								)}
							</Box>
						</Grid>
						<Grid item xs={12} md={5} lg={6}>
							<Box pr={{ sm: 6 }} pl={{ sm: 6 }}>
								<ContentRecommendation
									key={`SignInPageRegisterLink`}
									id={`SignInPageRegisterLink`}
									properties={{ emsName: `SignInPageRegisterLink` }}
								/>
							</Box>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
		</Grid>
	);
};
